import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { colors, fonts } from './UI'

const STEPS = [
  {
    target: '[data-walkthrough="sts"]',
    title: 'Safe to Spend',
    body: 'The real cash in your current accounts after bills and commitments. If you sweep your salary to savings or spend on a credit card day-to-day, this will sit low - that\'s completely normal. Use the spending tracker below it to see how you\'re tracking against your monthly budget.',
    tooltip: 'below',
    pad: 10,
  },
  {
    target: '[data-walkthrough="fab"]',
    title: 'Log a spend or income',
    body: 'For anything outside your regular bills - a coffee, a takeaway, a parking charge. Tap here, enter the amount, tag it to a category, and your Safe to Spend updates straight away.',
    tooltip: 'above',
    pad: 12,
  },
  {
    target: '[data-walkthrough="nav-budget"]',
    title: 'Budget',
    body: 'Set spending limits by category - Housing, Food, Transport. Bills you\'ve added automatically count against each category. No manual entry needed.',
    tooltip: 'above',
    pad: 8,
  },
  {
    target: '[data-walkthrough="nav-bills"]',
    title: 'Bills',
    body: 'Add recurring payments once. They appear in Upcoming Bills, count toward Safe to Spend, and mark as paid automatically on the due date.',
    tooltip: 'above',
    pad: 8,
  },
  {
    target: '[data-walkthrough="nav-goals"]',
    title: 'Saving money',
    body: 'Set savings goals in here - emergency fund, holiday, house deposit. When you actually make a transfer to savings, log it as a quick spend tagged to Savings. That way your budget shows it as done, and your spending strip reflects the full picture of where your money went.',
    tooltip: 'above',
    pad: 8,
  },
  {
    target: '[data-walkthrough="health-score"]',
    title: 'Financial Health',
    body: 'Your financial health scored 0-100 across emergency fund, savings rate, debt, goals and budget discipline. It improves automatically as you use the app.',
    tooltip: 'below',
    pad: 10,
  },
]

export default function WalkthroughOverlay({ onComplete }) {
  const [step, setStep] = useState(0)
  const [rect, setRect] = useState(null)

  const measure = useCallback((stepIndex) => {
    setRect(null)
    const s = STEPS[stepIndex]
    const el = document.querySelector(s.target)
    if (!el) {
      // Element not found - skip this step
      if (stepIndex < STEPS.length - 1) setStep(stepIndex + 1)
      else onComplete()
      return
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(() => {
      const r = el.getBoundingClientRect()
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
    }, 450)
  }, [onComplete])

  useEffect(() => { measure(step) }, [step, measure])

  function next() {
    if (step >= STEPS.length - 1) onComplete()
    else setStep(s => s + 1)
  }

  const current = STEPS[step]
  const pad = current.pad || 8
  const hl = rect ? {
    top: rect.top - pad,
    left: rect.left - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
    radius: 18,
  } : null

  // Position the card so its text is always readable and the buttons always reachable.
  // When there is room beside the highlight (small targets like the nav), sit in the
  // gap, clear of it. When the highlight is large and the gap is tight, pin to the
  // screen edge at a comfortable height and let the card gently overlap the dimmed
  // highlight - readability beats avoiding overlap.
  const vh = window.innerHeight
  const navH = 84      // bottom nav + breathing room
  const topInset = 16
  const gap = 12
  const minComfort = Math.round(vh * 0.34)   // enough for title + a few lines + buttons
  let anchor = { top: Math.round(vh / 2 - 140) }   // fallback before the rect is measured
  let cardMaxHeight = Math.round(vh * 0.62)
  if (rect) {
    const spaceAbove = rect.top - topInset - gap
    const spaceBelow = vh - (rect.top + rect.height) - navH - gap
    const placeAbove = spaceAbove > spaceBelow
    const room = Math.max(spaceAbove, spaceBelow)
    if (room >= minComfort) {
      // Comfortable gap beside the highlight - sit in it, clear of the highlight.
      cardMaxHeight = Math.min(Math.round(vh * 0.62), room)
      anchor = placeAbove
        ? { bottom: Math.round(vh - rect.top + gap) }
        : { top: Math.round(rect.top + rect.height + gap) }
    } else {
      // Large highlight, tight gap - pin to the edge with a readable height.
      cardMaxHeight = Math.min(minComfort, vh - topInset - navH - 8)
      anchor = placeAbove ? { top: topInset } : { bottom: navH }
    }
  }

  // Portal to <body> so the overlay escapes the scrolling <main> wrapper and paints
  // above the fixed bottom nav - otherwise the gold highlight box hides behind it.
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
      {/* Dimmed overlay - four panels around the highlight */}
      {hl ? (
        <>
          {/* Top */}
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: hl.top, background: 'rgba(0,0,0,0.8)' }} />
          {/* Bottom */}
          <div style={{ position: 'fixed', top: hl.top + hl.height, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)' }} />
          {/* Left */}
          <div style={{ position: 'fixed', top: hl.top, left: 0, width: hl.left, height: hl.height, background: 'rgba(0,0,0,0.8)' }} />
          {/* Right */}
          <div style={{ position: 'fixed', top: hl.top, left: hl.left + hl.width, right: 0, height: hl.height, background: 'rgba(0,0,0,0.8)' }} />
          {/* Highlight border */}
          <div style={{ position: 'fixed', top: hl.top, left: hl.left, width: hl.width, height: hl.height, borderRadius: hl.radius, border: `2px solid ${colors.gold}`, boxShadow: `0 0 0 1px rgba(201,151,43,0.3), 0 0 20px rgba(201,151,43,0.25)`, pointerEvents: 'none', zIndex: 1001 }} />
        </>
      ) : (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)' }} />
      )}

      {/* Tooltip card */}
      <div style={{
        position: 'fixed', left: '50%', transform: 'translateX(-50%)',
        width: 'calc(100% - 32px)', maxWidth: '448px',
        ...anchor,
        maxHeight: cardMaxHeight,
        display: 'flex', flexDirection: 'column',
        background: colors.cardMid,
        border: '1px solid rgba(201,151,43,0.3)',
        borderRadius: '18px',
        padding: '18px 20px',
        zIndex: 1002,
        boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
        overflow: 'hidden',
        transition: 'top 0.3s ease, bottom 0.3s ease',
      }}>
        {/* Progress bar */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '14px' }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{ flex: 1, height: '3px', borderRadius: '2px', background: i <= step ? colors.gold : 'rgba(255,255,255,0.12)', transition: 'background 0.3s' }} />
          ))}
        </div>

        <div style={{ fontSize: '17px', fontWeight: 800, color: colors.text, fontFamily: fonts.heading, marginBottom: '8px', letterSpacing: '-0.01em' }}>
          {current.title}
        </div>
        <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, lineHeight: 1.65, marginBottom: '18px', flex: '1 1 auto', minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {current.body}
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onComplete} style={{ padding: '11px 16px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', color: colors.muted, fontFamily: fonts.body, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            Skip
          </button>
          <button onClick={next} style={{ flex: 1, padding: '11px', background: colors.gold, border: 'none', borderRadius: '10px', color: colors.onAccent, fontFamily: fonts.heading, fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
            {step === STEPS.length - 1 ? 'Done ✓' : 'Got it →'}
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '12px', color: `rgba(${colors.textRgb},0.3)` }}>
          {step + 1} of {STEPS.length}
        </div>
      </div>
    </div>,
    document.body
  )
}
